import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isNative } from "../lib/native";

/**
 * Capacitor Universal-Link handler.
 *
 * When the iOS app is registered as the handler for `https://zcardon.com/*`
 * (via `apple-app-site-association` + the `applinks:` entitlement), tapping a
 * Zcardon link from anywhere on iPhone — Mail, Messages, Safari — wakes the
 * native app with an `appUrlOpen` event carrying the full URL.
 *
 * We strip the host and forward the path + query + hash to React Router so
 * the existing in-app routes handle the navigation. Result: a recipient who
 * taps `https://zcardon.com/pay?last4=9999&exp=12%2F28&s=scan` lands on the
 * tap-to-pay screen *inside the installed app* instead of bouncing through
 * Safari.
 *
 * No-op when running in the browser (native bridge isn't loaded). Listener
 * is removed on unmount to avoid leaks during HMR / fast-refresh.
 */
export default function DeepLinkHandler() {
    const nav = useNavigate();

    useEffect(() => {
        if (!isNative()) return;
        let handle = null;
        let cancelled = false;

        (async () => {
            try {
                const { App } = await import("@capacitor/app");
                handle = await App.addListener("appUrlOpen", (event) => {
                    if (cancelled || !event?.url) return;
                    try {
                        const u = new URL(event.url);
                        // Refuse paths that aren't ours — paranoid but cheap.
                        const allowed = ["zcardon.com", "www.zcardon.com"];
                        if (u.hostname && !allowed.includes(u.hostname.toLowerCase())) {
                            return;
                        }
                        const target = `${u.pathname || "/"}${u.search || ""}${u.hash || ""}`;
                        nav(target);
                    } catch {
                        // Bad URL — let iOS decide what to do.
                    }
                });
            } catch {
                /* @capacitor/app not available — running in browser */
            }
        })();

        return () => {
            cancelled = true;
            if (handle && typeof handle.remove === "function") {
                try { handle.remove(); } catch { /* noop */ }
            }
        };
    }, [nav]);

    return null;
}
